import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import apiService from '../services/ApiService';
import { useToast } from '../contexts/ToastContext';
import PageHeader from '../components/shell/PageHeader';
import MessageEditor from '../components/messages/MessageEditor';
import RecipientPicker from '../components/messages/RecipientPicker';
import {
    MESSAGE_STATUS, MESSAGE_TYPES, MessageContent, messagePlainText, messageTypeMeta,
} from '../components/messages/messageContent';
import type { AdminMessageDetail, AdminMessageUpsert } from '../types/messages';
import type { AdminUserListItem } from '../types/users';
import { formatDateTime, formatRelative, fromLocalInputValue, initialsOf, toLocalInputValue } from '../utils/format';
import '../styles/messages.scss';

/**
 * Scrittura e modifica di una comunicazione. A sinistra il modulo, a destra
 * l'anteprima con la stessa resa della bacheca del portale; per una
 * comunicazione esistente anche chi l'ha letta.
 */
interface FormState {
    title: string;
    content: string;
    messageType: string;
    audience: 'all' | 'selected';
    recipients: number[];
    publishMode: 'now' | 'at';
    publishAt: string;
    hasExpiry: boolean;
    expiresAt: string;
}

const EMPTY: FormState = {
    title: '',
    content: '',
    messageType: 'Info',
    audience: 'all',
    recipients: [],
    publishMode: 'now',
    publishAt: '',
    hasExpiry: false,
    expiresAt: '',
};

// Una bozza senza data futura si pubblica "subito"; le altre conservano la loro data.
const fromDetail = (m: AdminMessageDetail): FormState => ({
    title: m.title,
    content: m.content,
    messageType: m.messageType,
    audience: m.isGlobal ? 'all' : 'selected',
    recipients: m.isGlobal ? [] : m.recipients.map(r => r.userId),
    publishMode: !m.isActive && new Date(m.publishedAt).getTime() <= Date.now() ? 'now' : 'at',
    publishAt: toLocalInputValue(m.publishedAt),
    hasExpiry: !!m.expiresAt,
    expiresAt: toLocalInputValue(m.expiresAt),
});

const MessageEditPage: React.FC = () => {
    const { id } = useParams();
    const isNew = !id || id === 'new';
    const messageId = isNew ? null : Number(id);
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [searchParams] = useSearchParams();
    // /messages/new?to=12 arriva dalla scheda utente: destinatario gia' scelto.
    const presetTo = isNew ? Number(searchParams.get('to')) || null : null;
    const start: FormState = presetTo ? { ...EMPTY, audience: 'selected', recipients: [presetTo] } : EMPTY;

    const [form, setForm] = useState<FormState>(start);
    const [initial, setInitial] = useState<FormState>(start);
    const [detail, setDetail] = useState<AdminMessageDetail | null>(null);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [users, setUsers] = useState<AdminUserListItem[]>([]);
    const [usersLoading, setUsersLoading] = useState(true);

    const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm(f => ({ ...f, [key]: value }));

    useEffect(() => {
        apiService.adminGetUsers<AdminUserListItem>()
            .then(setUsers)
            .catch(() => showToast('Errore', 'Impossibile caricare gli utenti', 'danger'))
            .finally(() => setUsersLoading(false));
    }, [showToast]);

    const load = useCallback(async () => {
        if (messageId === null) return;
        setLoading(true);
        try {
            const m = await apiService.adminGetMessage<AdminMessageDetail>(messageId);
            setDetail(m);
            const state = fromDetail(m);
            setForm(state);
            setInitial(state);
        } catch {
            showToast('Errore', 'Comunicazione non trovata', 'danger');
            navigate('/messages');
        } finally {
            setLoading(false);
        }
    }, [messageId, navigate, showToast]);
    useEffect(() => { void load(); }, [load]);

    const dirty = JSON.stringify(form) !== JSON.stringify(initial);
    useEffect(() => {
        if (!dirty) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [dirty]);

    const publishIso = form.publishMode === 'now' ? null : fromLocalInputValue(form.publishAt);
    const expiresIso = form.hasExpiry ? fromLocalInputValue(form.expiresAt) : null;
    const plain = messagePlainText(form.content);

    const errors = useMemo(() => {
        const e: Partial<Record<'title' | 'content' | 'recipients' | 'publishAt' | 'expiresAt', string>> = {};
        if (!form.title.trim()) e.title = 'Scrivi un titolo.';
        else if (form.title.trim().length > 200) e.title = 'Massimo 200 caratteri.';
        if (!plain) e.content = 'Scrivi il testo della comunicazione.';
        if (form.audience === 'selected' && form.recipients.length === 0) e.recipients = 'Scegli almeno un destinatario.';
        if (form.publishMode === 'at' && !form.publishAt) e.publishAt = 'Indica quando pubblicarla.';
        if (form.hasExpiry) {
            if (!form.expiresAt) e.expiresAt = 'Indica la data di scadenza.';
            else {
                const start = publishIso ? new Date(publishIso).getTime() : Date.now();
                if (new Date(form.expiresAt).getTime() <= start) e.expiresAt = 'La scadenza deve venire dopo la pubblicazione.';
            }
        }
        return e;
    }, [form, plain, publishIso]);
    const hasErrors = Object.keys(errors).length > 0;

    const isFuture = !!publishIso && new Date(publishIso).getTime() > Date.now();
    const primaryLabel = isFuture ? 'Programma' : detail?.isActive ? 'Salva modifiche' : 'Pubblica';

    const save = async (active: boolean) => {
        setSubmitted(true);
        // Una bozza puo' restare senza destinatari o date, ma serve comunque titolo e testo.
        if (active ? hasErrors : (errors.title || errors.content)) {
            showToast('Controlla il modulo', 'Alcuni campi vanno completati prima di salvare.', 'warning');
            return;
        }
        const dto: AdminMessageUpsert = {
            title: form.title.trim(),
            content: form.content,
            messageType: form.messageType,
            isGlobal: form.audience === 'all',
            isActive: active,
            publishedAt: publishIso,
            expiresAt: expiresIso,
            recipientUserIds: form.audience === 'selected' ? form.recipients : null,
        };
        setSaving(true);
        try {
            const saved = messageId === null
                ? await apiService.adminCreateMessage<AdminMessageDetail>(dto)
                : await apiService.adminUpdateMessage<AdminMessageDetail>(messageId, dto);
            const state = fromDetail(saved);
            setDetail(saved);
            setForm(state);
            setInitial(state);
            setSubmitted(false);
            showToast(
                !active ? 'Bozza salvata' : saved.status === 'Scheduled' ? 'Comunicazione programmata' : 'Comunicazione pubblicata',
                !active ? 'Non è visibile agli utenti finché non la pubblichi.'
                    : saved.status === 'Scheduled' ? `Comparirà in bacheca ${formatRelative(saved.publishedAt)}.`
                        : 'È visibile nella bacheca della dashboard.',
                'success');
            if (messageId === null) navigate(`/messages/${saved.id}`, { replace: true });
        } catch (e: any) {
            showToast('Errore', e?.response?.data?.error ?? 'Salvataggio non riuscito', 'danger');
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (messageId === null) return;
        setSaving(true);
        try {
            await apiService.adminDeleteMessage(messageId);
            showToast('Comunicazione eliminata', 'Rimossa anche dallo storico degli utenti.', 'success');
            setInitial(form);
            navigate('/messages');
        } catch {
            showToast('Errore', 'Eliminazione non riuscita', 'danger');
            setSaving(false);
        }
    };

    if (loading) return <div className="et-state py-5"><span className="spinner-border spinner-border-sm" /> Caricamento…</div>;

    const type = messageTypeMeta(form.messageType);
    const status = detail ? MESSAGE_STATUS[detail.status] : null;
    const showError = (k: keyof typeof errors) => submitted && errors[k];
    const previewDate = publishIso ?? new Date().toISOString();

    return (
        <>
            <Link to="/messages" className="btn btn-sm btn-link p-0 mb-2">
                <i className="bi bi-arrow-left me-1" />Tutte le comunicazioni
            </Link>
            <PageHeader
                kicker="Comunicazioni"
                title={isNew ? 'Nuova comunicazione' : detail?.title || 'Comunicazione'}
                meta={detail && <>
                    {status && <span className={`badge ${status.badge}`}><i className={`bi ${status.icon} me-1`} />{status.label}</span>}
                    <span>Creata {formatDateTime(detail.createdAt)}{detail.createdByEmail ? ` da ${detail.createdByEmail}` : detail.isSystem ? ' dal sistema' : ''}</span>
                    {dirty && <span className="text-warning-emphasis"><i className="bi bi-dot" />Modifiche non salvate</span>}
                </>}
                actions={!isNew && (
                    <button className="btn btn-outline-danger btn-sm" onClick={() => setConfirmDelete(true)} disabled={saving}>
                        <i className="bi bi-trash me-1" />Elimina
                    </button>
                )}
            />

            <div className="msg-edit">
                <div className="msg-edit__form">
                    <section className="card shadow-sm">
                        <div className="card-body">
                            <div className="mb-3">
                                <label className="form-label" htmlFor="msg-title">Titolo</label>
                                <input id="msg-title" className={`form-control ${showError('title') ? 'is-invalid' : ''}`}
                                    value={form.title} maxLength={200}
                                    placeholder="Es. Nuovo tool: Gestione Listini"
                                    onChange={e => set('title', e.target.value)} />
                                <div className="d-flex justify-content-between">
                                    <div className="invalid-feedback d-block">{showError('title')}</div>
                                    <small className="text-body-secondary text-nowrap">{form.title.length}/200</small>
                                </div>
                            </div>

                            <div className="mb-3">
                                <span className="form-label d-block">Tipo</span>
                                <div className="msg-type-picker" role="radiogroup" aria-label="Tipo di comunicazione">
                                    {MESSAGE_TYPES.map(t => (
                                        <button key={t.value} type="button" role="radio" aria-checked={form.messageType === t.value}
                                            className={`msg-type-picker__opt is-${t.value.toLowerCase()} ${form.messageType === t.value ? 'is-active' : ''}`}
                                            onClick={() => set('messageType', t.value)}>
                                            <i className={`bi ${t.icon}`} />
                                            <span><strong>{t.label}</strong><small>{t.hint}</small></span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="form-label" htmlFor="msg-content">Testo</label>
                                <MessageEditor id="msg-content" value={form.content} onChange={v => set('content', v)}
                                    invalid={!!showError('content')} placeholder="Cosa devono sapere gli utenti?" />
                                {showError('content') && <div className="invalid-feedback d-block">{errors.content}</div>}
                            </div>
                        </div>
                    </section>

                    <section className="card shadow-sm">
                        <div className="card-header">Destinatari</div>
                        <div className="card-body">
                            <div className="et-segmented mb-3" role="radiogroup" aria-label="Destinatari">
                                <button type="button" role="radio" aria-checked={form.audience === 'all'}
                                    className={form.audience === 'all' ? 'is-active' : ''} onClick={() => set('audience', 'all')}>
                                    <i className="bi bi-globe2 me-1" />Tutti gli utenti
                                </button>
                                <button type="button" role="radio" aria-checked={form.audience === 'selected'}
                                    className={form.audience === 'selected' ? 'is-active' : ''} onClick={() => set('audience', 'selected')}>
                                    <i className="bi bi-people me-1" />Utenti selezionati
                                </button>
                            </div>
                            {form.audience === 'all' ? (
                                <p className="text-body-secondary small mb-0">
                                    La vedranno tutti gli utenti con accesso al portale, compresi quelli approvati in seguito.
                                    {!usersLoading && <> Oggi sono <strong className="text-body">{users.filter(u => u.approvalStatus === 'Approved' && u.isActive).length}</strong>.</>}
                                </p>
                            ) : (
                                <>
                                    <RecipientPicker users={users} loading={usersLoading}
                                        selected={form.recipients} onChange={ids => set('recipients', ids)} />
                                    {showError('recipients') && <div className="invalid-feedback d-block">{errors.recipients}</div>}
                                </>
                            )}
                        </div>
                    </section>

                    <section className="card shadow-sm">
                        <div className="card-header">Pubblicazione</div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <span className="form-label d-block">Quando</span>
                                    <div className="form-check">
                                        <input className="form-check-input" type="radio" id="pub-now" name="pub"
                                            checked={form.publishMode === 'now'} onChange={() => set('publishMode', 'now')} />
                                        <label className="form-check-label" htmlFor="pub-now">Subito</label>
                                    </div>
                                    <div className="form-check">
                                        <input className="form-check-input" type="radio" id="pub-at" name="pub"
                                            checked={form.publishMode === 'at'} onChange={() => set('publishMode', 'at')} />
                                        <label className="form-check-label" htmlFor="pub-at">Dal giorno…</label>
                                    </div>
                                    {form.publishMode === 'at' && (
                                        <>
                                            <input type="datetime-local" className={`form-control form-control-sm mt-2 ${showError('publishAt') ? 'is-invalid' : ''}`}
                                                aria-label="Data di pubblicazione"
                                                value={form.publishAt} onChange={e => set('publishAt', e.target.value)} />
                                            <div className="invalid-feedback d-block">{showError('publishAt')}</div>
                                        </>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <span className="form-label d-block">Scadenza</span>
                                    <div className="form-check form-switch">
                                        <input className="form-check-input" type="checkbox" id="has-expiry"
                                            checked={form.hasExpiry} onChange={e => set('hasExpiry', e.target.checked)} />
                                        <label className="form-check-label" htmlFor="has-expiry">Togli dalla bacheca il…</label>
                                    </div>
                                    {form.hasExpiry && (
                                        <>
                                            <input type="datetime-local" className={`form-control form-control-sm mt-2 ${showError('expiresAt') ? 'is-invalid' : ''}`}
                                                aria-label="Data di scadenza"
                                                value={form.expiresAt} onChange={e => set('expiresAt', e.target.value)} />
                                            <div className="invalid-feedback d-block">{showError('expiresAt')}</div>
                                        </>
                                    )}
                                    {!form.hasExpiry && <small className="text-body-secondary">Resta in bacheca finché non la ritiri.</small>}
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                <aside className="msg-edit__side">
                    <section className="msg-preview">
                        <div className="msg-preview__label"><i className="bi bi-eye me-1" />Anteprima nella bacheca</div>
                        <div className="msg-preview__board">
                            <span className="msg-preview__eyebrow">IL FILO DIRETTO CON SEMPLORA</span>
                            <div className="msg-preview__heading">La bacheca</div>
                            <article className={`msg-preview__notice is-${form.messageType.toLowerCase()}`}>
                                <div className="msg-preview__meta">
                                    <span>{type.label}</span>
                                    <time>{new Date(previewDate).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}</time>
                                    <em>Da leggere</em>
                                </div>
                                <div className="msg-preview__title">{form.title.trim() || 'Titolo della comunicazione'}</div>
                                {plain
                                    ? <MessageContent content={form.content} className="msg-preview__body" />
                                    : <div className="msg-preview__body is-empty">Il testo comparirà qui.</div>}
                            </article>
                        </div>
                    </section>

                    {detail && (
                        <section className="card shadow-sm">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <span>{detail.isGlobal ? 'Chi l’ha letta' : 'Destinatari'}</span>
                                <span className="small text-body-secondary fw-normal">
                                    {detail.readCount} su {detail.recipientCount}
                                </span>
                            </div>
                            <ul className="list-group list-group-flush msg-readers">
                                {detail.recipients.length === 0 && (
                                    <li className="list-group-item text-body-secondary small">Nessuna lettura per ora.</li>
                                )}
                                {detail.recipients.map(r => (
                                    <li key={r.userId} className="list-group-item">
                                        <Link to={`/users/${r.userId}`} className="msg-readers__row">
                                            <span className="user-avatar is-sm">{initialsOf(r.email, r.displayName)}</span>
                                            <span className="msg-readers__text">
                                                <strong>{r.displayName || r.email}</strong>
                                                {r.displayName && <small>{r.email}</small>}
                                            </span>
                                            {r.isRead
                                                ? <span className="badge bg-success" title={formatDateTime(r.readAt)}>Letta {formatRelative(r.readAt)}</span>
                                                : <span className="badge bg-light">Da leggere</span>}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                </aside>
            </div>

            <div className="msg-edit__bar">
                <span className="msg-edit__bar-status">
                    {isFuture
                        ? <><i className="bi bi-calendar-event me-1" />Uscirà {formatRelative(publishIso)}</>
                        : form.audience === 'all'
                            ? <><i className="bi bi-globe2 me-1" />Per tutti gli utenti</>
                            : <><i className="bi bi-people me-1" />Per {form.recipients.length} {form.recipients.length === 1 ? 'utente' : 'utenti'}</>}
                </span>
                <button className="btn btn-outline-secondary" onClick={() => void save(false)} disabled={saving}>
                    {detail?.isActive ? 'Ritira e salva come bozza' : 'Salva bozza'}
                </button>
                <button className="btn btn-primary" onClick={() => void save(true)} disabled={saving}>
                    {saving ? <span className="spinner-border spinner-border-sm me-2" /> : <i className={`bi ${isFuture ? 'bi-calendar-check' : 'bi-send'} me-2`} />}
                    {primaryLabel}
                </button>
            </div>

            <Modal show={confirmDelete} onHide={() => setConfirmDelete(false)} centered>
                <Modal.Header closeButton><Modal.Title>Eliminare la comunicazione?</Modal.Title></Modal.Header>
                <Modal.Body>
                    <p className="mb-2"><strong>{detail?.title}</strong></p>
                    <p className="text-body-secondary small mb-0">
                        Sparisce dalla bacheca e dallo storico degli utenti, insieme alle letture registrate.
                        Per toglierla solo dalla bacheca usa <em>Ritira</em>.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-outline-secondary" onClick={() => setConfirmDelete(false)}>Annulla</button>
                    <button className="btn btn-danger" onClick={() => void remove()} disabled={saving}>Elimina definitivamente</button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default MessageEditPage;
